/**
 * Training plan — every skill gap turned into a dated task with a trainer
 * nominated from the skill matrix.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  GraduationCap, Loader2, ArrowLeft, Wand2, CheckCircle2, Trash2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';
import { LevelChip } from './shared';

const STATUS_STYLE = {
  open: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-sky-100 text-sky-700',
  done: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-100 text-slate-400',
};

const TrainingPlanPage = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState({ tasks: [], summary: {}, today: '' });
  const [trainers, setTrainers] = useState({});   // { skillId: [{id,name}] }
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const { data: d } = await axios.get(`${API}/skills/training${qs}`, { headers: authHeader() });
      setData(d);
      const ids = [...new Set((d.tasks || []).map((t) => t.skill_id))];
      const pairs = await Promise.all(ids.map(async (id) => {
        try {
          const { data: t } = await axios.get(`${API}/skills/trainers/${id}`, { headers: authHeader() });
          return [id, t.trainers || []];
        } catch { return [id, []]; }
      }));
      setTrainers(Object.fromEntries(pairs));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy('gen');
    try {
      const { data: r } = await axios.post(`${API}/skills/training/generate`, {
        include_needs_improvement: true, due_in_days: 30,
      }, { headers: authHeader() });
      toast.success(`${r.created} task(s) created (due ${r.due_date}) · ${r.skipped_existing} already open`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const patch = async (task, body) => {
    setBusy(task.id);
    try {
      await axios.patch(`${API}/skills/training/${task.id}`, body, { headers: authHeader() });
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const complete = async (task) => {
    setBusy(task.id);
    try {
      await axios.post(`${API}/skills/training/${task.id}/complete`, {
        outcome_level: 'ok',
      }, { headers: authHeader() });
      toast.success('Training marked done — record it in the next assessment to lift the level');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const remove = async (task) => {
    if (!window.confirm(`Delete the ${task.skill_name} task for ${task.staff_name}?`)) return;
    setBusy(task.id);
    try {
      await axios.delete(`${API}/skills/training/${task.id}`, { headers: authHeader() });
      toast.success('Task deleted');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const s = data.summary || {};

  return (
    <div className="space-y-5 pb-20" data-testid="training-plan-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/skills" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1">
            <ArrowLeft size={14} /> Skill matrix
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <GraduationCap size={20} className="text-teal-600" /> Training plan
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Every skill gap as a dated task, with a trainer nominated from the matrix.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={`${inputCls} !w-auto`} value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} data-testid="training-status-filter"
          >
            <option value="">All statuses</option>
            {['open', 'in_progress', 'done', 'cancelled'].map((st) => (
              <option key={st} value={st}>{st.replace('_', ' ')}</option>
            ))}
          </select>
          <button
            onClick={generate} disabled={busy === 'gen'}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
            data-testid="generate-tasks-btn"
          >
            {busy === 'gen' ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
            Generate from gaps
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Open', value: s.open ?? 0, testid: 'stat-open' },
          { label: 'In progress', value: s.in_progress ?? 0, testid: 'stat-progress' },
          { label: 'Overdue', value: s.overdue ?? 0, testid: 'stat-overdue' },
          { label: 'Completed', value: s.done ?? 0, testid: 'stat-done' },
        ].map((k) => (
          <div key={k.label} className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={k.testid}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.label}</div>
            <div className="text-lg font-semibold text-slate-900">{k.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="p-12 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : !data.tasks.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="no-tasks">
          No training tasks. Use <span className="font-medium">Generate from gaps</span> to create
          them from the latest signed assessments.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="training-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Staff</th>
                <th className="text-left px-4 py-2">Skill</th>
                <th className="text-center px-3 py-2">Now → Target</th>
                <th className="text-left px-3 py-2">Trainer</th>
                <th className="text-left px-3 py-2">Due</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.tasks.map((t) => (
                <tr key={t.id} className={t.overdue ? 'bg-red-50/40' : 'hover:bg-slate-50'} data-testid={`task-row-${t.id}`}>
                  <td className="px-4 py-2">
                    <Link to={`/admin/skills/staff/${t.staff_id}`} className="font-medium text-slate-900 hover:text-teal-700">
                      {t.staff_name}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-slate-800">{t.skill_name}</div>
                    <div className="text-xs text-slate-400">{t.skill_group}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <LevelChip level={t.current_level} size="sm" />
                      <span className="text-slate-400">→</span>
                      <LevelChip level={t.target_level} size="sm" />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className={`${inputCls} !py-1 !w-52 truncate`} value={t.trainer_id || ''}
                      onChange={(e) => patch(t, { trainer_id: e.target.value })}
                      disabled={busy === t.id}
                      data-testid={`task-trainer-${t.id}`}
                    >
                      <option value="">— pick trainer —</option>
                      {(trainers[t.skill_id] || []).map((tr) => (
                        <option key={tr.id} value={tr.id}>◎ {tr.name}</option>
                      ))}
                      {t.trainer_id && !(trainers[t.skill_id] || []).some((x) => x.id === t.trainer_id) && (
                        <option value={t.trainer_id}>{t.trainer_name}</option>
                      )}
                    </select>
                    {!(trainers[t.skill_id] || []).length && (
                      <div className="text-[10px] text-amber-700 mt-0.5 inline-flex items-center gap-0.5">
                        <AlertTriangle size={10} /> no in-house expert yet
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="date" className={`${inputCls} !py-1 !w-36`} value={t.due_date || ''}
                      onChange={(e) => patch(t, { due_date: e.target.value })}
                      disabled={busy === t.id}
                      data-testid={`task-due-${t.id}`}
                    />
                    {t.overdue && <div className="text-[10px] text-red-600 mt-0.5">overdue</div>}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className={`${inputCls} !py-1 !w-32`} value={t.status}
                      onChange={(e) => patch(t, { status: e.target.value })}
                      disabled={busy === t.id}
                      data-testid={`task-status-${t.id}`}
                    >
                      {['open', 'in_progress', 'done', 'cancelled'].map((st) => (
                        <option key={st} value={st}>{st.replace('_', ' ')}</option>
                      ))}
                    </select>
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${STATUS_STYLE[t.status]}`}>
                      {t.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {t.status !== 'done' && (
                      <button
                        onClick={() => complete(t)} disabled={busy === t.id}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        data-testid={`task-complete-${t.id}`}
                      >
                        <CheckCircle2 size={12} /> Done
                      </button>
                    )}
                    {data.is_hr && (
                      <button
                        onClick={() => remove(t)} disabled={busy === t.id}
                        className="ml-1.5 p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`task-delete-${t.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrainingPlanPage;
